import { useCallback, useState } from "react";
import { Button, Group, Modal, Stack } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { normalizeDateOnlyQueryRange } from "../../services/dateOnlyQuery";
import { parseDateOnly, type DateOnlyString } from "../../utils/dateOnly";

export interface CorrectionsQueueFilters {
  dateFrom: DateOnlyString | null;
  dateTo: DateOnlyString | null;
  dateReceivedFrom: DateOnlyString | null;
  dateReceivedTo: DateOnlyString | null;
  dateCollectedFrom: DateOnlyString | null;
  dateCollectedTo: DateOnlyString | null;
  dateReportedFrom: DateOnlyString | null;
  dateReportedTo: DateOnlyString | null;
}

export const EMPTY_CORRECTIONS_QUEUE_FILTERS: CorrectionsQueueFilters = {
  dateFrom: null,
  dateTo: null,
  dateReceivedFrom: null,
  dateReceivedTo: null,
  dateCollectedFrom: null,
  dateCollectedTo: null,
  dateReportedFrom: null,
  dateReportedTo: null,
};

const FILTER_LABELS: Record<keyof CorrectionsQueueFilters, string> = {
  dateFrom: "Corrected from",
  dateTo: "Corrected to",
  dateReceivedFrom: "Date Received from",
  dateReceivedTo: "Date Received to",
  dateCollectedFrom: "Date Collected from",
  dateCollectedTo: "Date Collected to",
  dateReportedFrom: "Date Reported from",
  dateReportedTo: "Date Reported to",
};

interface Props {
  filters: CorrectionsQueueFilters;
  onApply: (filters: CorrectionsQueueFilters) => void;
  onClose: () => void;
}

export function CorrectionsQueueDateFilters({
  filters,
  onApply,
  onClose,
}: Props) {
  const [pending, setPending] = useState<CorrectionsQueueFilters>({
    ...filters,
  });
  const [error, setError] = useState<string | null>(null);

  const updateDate = useCallback(
    (field: keyof CorrectionsQueueFilters, value: unknown) => {
      if (value === null) {
        setPending((current) => ({ ...current, [field]: null }));
        setError(null);
        return;
      }
      const parsed = parseDateOnly(value);
      if (!parsed) {
        setError(`Select a valid ${FILTER_LABELS[field]} date.`);
        return;
      }
      setPending((current) => ({ ...current, [field]: parsed }));
      setError(null);
    },
    []
  );

  const handleDateFrom = useCallback(
    (value: unknown) => updateDate("dateFrom", value),
    [updateDate]
  );
  const handleDateTo = useCallback(
    (value: unknown) => updateDate("dateTo", value),
    [updateDate]
  );
  const handleReceivedFrom = useCallback(
    (value: unknown) => updateDate("dateReceivedFrom", value),
    [updateDate]
  );
  const handleReceivedTo = useCallback(
    (value: unknown) => updateDate("dateReceivedTo", value),
    [updateDate]
  );
  const handleCollectedFrom = useCallback(
    (value: unknown) => updateDate("dateCollectedFrom", value),
    [updateDate]
  );
  const handleCollectedTo = useCallback(
    (value: unknown) => updateDate("dateCollectedTo", value),
    [updateDate]
  );
  const handleReportedFrom = useCallback(
    (value: unknown) => updateDate("dateReportedFrom", value),
    [updateDate]
  );
  const handleReportedTo = useCallback(
    (value: unknown) => updateDate("dateReportedTo", value),
    [updateDate]
  );

  const handleApply = useCallback(() => {
    try {
      (
        [
          [pending.dateFrom, pending.dateTo, "Corrected from", "Corrected to"],
          [
            pending.dateReceivedFrom,
            pending.dateReceivedTo,
            "Date Received from",
            "Date Received to",
          ],
          [
            pending.dateCollectedFrom,
            pending.dateCollectedTo,
            "Date Collected from",
            "Date Collected to",
          ],
          [
            pending.dateReportedFrom,
            pending.dateReportedTo,
            "Date Reported from",
            "Date Reported to",
          ],
        ] as const
      ).forEach(([from, to, fromLabel, toLabel]) => {
        normalizeDateOnlyQueryRange(
          { from: from ?? undefined, to: to ?? undefined },
          {
            from: fromLabel,
            to: toLabel,
          }
        );
      });
      onApply(pending);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? `${caught.message.replace(
              "must not be after",
              "must be before or equal to"
            )}.`
          : "Select valid date filters."
      );
    }
  }, [onApply, pending]);

  const handleClear = useCallback(() => {
    setPending(EMPTY_CORRECTIONS_QUEUE_FILTERS);
    setError(null);
  }, []);

  return (
    <Modal opened onClose={onClose} title="Filter Corrections" size="sm">
      <Stack gap="md">
        <DatePickerInput
          label="Corrected from"
          placeholder="Any start date"
          value={pending.dateFrom}
          onChange={handleDateFrom}
          clearable
          maxDate={pending.dateTo ?? undefined}
        />
        <DatePickerInput
          label="Corrected to"
          placeholder="Any end date"
          value={pending.dateTo}
          onChange={handleDateTo}
          clearable
          minDate={pending.dateFrom ?? undefined}
        />
        <DatePickerInput
          label="Date Received from"
          placeholder="Any start date"
          value={pending.dateReceivedFrom}
          onChange={handleReceivedFrom}
          clearable
          maxDate={pending.dateReceivedTo ?? undefined}
        />
        <DatePickerInput
          label="Date Received to"
          placeholder="Any end date"
          value={pending.dateReceivedTo}
          onChange={handleReceivedTo}
          clearable
          minDate={pending.dateReceivedFrom ?? undefined}
        />
        <DatePickerInput
          label="Date Collected from"
          placeholder="Any start date"
          value={pending.dateCollectedFrom}
          onChange={handleCollectedFrom}
          clearable
          maxDate={pending.dateCollectedTo ?? undefined}
        />
        <DatePickerInput
          label="Date Collected to"
          placeholder="Any end date"
          value={pending.dateCollectedTo}
          onChange={handleCollectedTo}
          clearable
          minDate={pending.dateCollectedFrom ?? undefined}
        />
        <DatePickerInput
          label="Date Reported from"
          placeholder="Any start date"
          value={pending.dateReportedFrom}
          onChange={handleReportedFrom}
          clearable
          maxDate={pending.dateReportedTo ?? undefined}
        />
        <DatePickerInput
          label="Date Reported to"
          placeholder="Any end date"
          value={pending.dateReportedTo}
          onChange={handleReportedTo}
          clearable
          minDate={pending.dateReportedFrom ?? undefined}
          error={error}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" color="gray" size="sm" onClick={handleClear}>
            Clear
          </Button>
          <Button size="sm" onClick={handleApply}>
            Apply Filters
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
